<script setup>
import { computed } from 'vue';
import { Handle, Position, useNode } from '@vue-flow/core';
import { useI18n } from 'vue-i18n';
import { useNodeAudience } from '../../composables/useNodeAudience.js';

defineProps({
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const { id } = useNode();
const audienceCount = useNodeAudience(id);

const TABLE_LABELS = computed(() => ({
  contacts:             t('FLOW_BUILDER.NODES.QUERY_DATABASE.TABLE_CONTACTS'),
  conversations:        t('FLOW_BUILDER.NODES.QUERY_DATABASE.TABLE_CONVERSATIONS'),
  orders:               t('FLOW_BUILDER.NODES.QUERY_DATABASE.TABLE_ORDERS'),
  catalog_products:     t('FLOW_BUILDER.NODES.QUERY_DATABASE.TABLE_CATALOG_PRODUCTS'),
  catalog_set_products: t('FLOW_BUILDER.NODES.QUERY_DATABASE.TABLE_CATALOG_SET_PRODUCTS'),
}));
</script>

<template>
  <div
    class="flow-node w-[180px] rounded-xl shadow-md border-2 transition-all"
    :class="selected ? 'border-orange-400' : 'border-transparent'"
  >
    <Handle type="target" :position="Position.Top" />

    <div class="flex items-center gap-2 px-3 py-2 bg-orange-500 text-white rounded-t-xl">
      <span class="i-lucide-database size-4 shrink-0" />
      <span class="text-xs font-semibold truncate flex-1">{{ data.name || t('FLOW_BUILDER.NODES.QUERY_DATABASE.TITLE') }}</span>
      <span
        v-if="audienceCount !== null"
        class="text-[9px] bg-white/20 rounded-full px-1.5 py-0.5 font-medium tabular-nums shrink-0"
      >
        {{ audienceCount }}
      </span>
    </div>

    <div class="px-3 py-2 bg-n-solid-1 text-n-slate-12 text-xs">
      <template v-if="data.table">
        <span>{{ TABLE_LABELS[data.table] || data.table }}</span>
        <span v-if="data.conditions?.length" class="text-n-slate-9">
          {{ ` · ${t('FLOW_BUILDER.NODES.QUERY_DATABASE.FILTERS_COUNT', data.conditions.length)}` }}
        </span>
      </template>
      <span v-else class="text-n-slate-9">{{ t('FLOW_BUILDER.NODES.QUERY_DATABASE.CONFIGURE') }}</span>
    </div>

    <div class="bg-n-solid-1 flex justify-between px-[18%] pt-1 pb-3 rounded-b-xl">
      <span class="text-[10px] text-n-slate-9">{{ t('FLOW_BUILDER.NODES.QUERY_DATABASE.FOUND') }}</span>
      <span class="text-[10px] text-n-slate-9">{{ t('FLOW_BUILDER.NODES.QUERY_DATABASE.NOT_FOUND') }}</span>
    </div>

    <Handle
      id="found"
      type="source"
      :position="Position.Bottom"
      class="!bg-n-teal-9 !border-n-teal-7"
      style="left: 25%"
    />
    <Handle
      id="not_found"
      type="source"
      :position="Position.Bottom"
      class="!bg-n-ruby-9 !border-n-ruby-7"
      style="left: 75%"
    />
  </div>
</template>
